import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { keyframes } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { Option } from '@/schemas/sceneDataSchema';

export interface SearchBarComponent {
  label: string;
  options: Option[];
}

export interface SearchBarProps {
  component: SearchBarComponent;
}

const filterOptions = createFilterOptions<Option>({
  matchFrom: 'any',
  stringify: (option) => option.label,
});

// Replaces default MUI sorting to order startsWith before contains
const customFilterOptions = (options: Option[], params: Parameters<typeof filterOptions>[1]) => {
  const filtered = filterOptions(options, params);

  if (!params.inputValue) return filtered;

  const inputValue = params.inputValue.toLowerCase();

  return filtered.sort((a, b) => {
    const aStartsWith = a.label.toLowerCase().startsWith(inputValue);
    const bStartsWith = b.label.toLowerCase().startsWith(inputValue);

    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    return 0;
  });
};

export const SearchBar = ({ component }: SearchBarProps) => {
  const scrollToId = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;

    // Retrigger the highlight animation
    element.classList.remove('scroll-highlight');
    void element.offsetWidth;
    element.classList.add('scroll-highlight');

    // Scroll to the element
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => {
      element.classList.remove('scroll-highlight');
    }, SCROLL_HIGHLIGHT_DURATION);
  };

  return (
    <>
      <GlobalStyles
        styles={(theme) => ({
          '.scroll-highlight': {
            '--search-highlight-color': theme.palette.primary.main,
            outline: `2px solid ${theme.palette.primary.main}`,
            animation: `${outlineFade} 4s ease-out`,
            scrollMarginTop: `${SCROLL_HIGHLIGHT_OFFSET}px`,
          },
        })}
      />
      <FormControl fullWidth>
        <Autocomplete<Option>
          options={component.options}
          filterOptions={customFilterOptions}
          getOptionLabel={(option) => option.label}
          onChange={(_, option) => {
            if (option?.value) {
              scrollToId(option.value.toString());
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={component.label}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const typed = (e.target as HTMLInputElement).value.trim().toLowerCase();
                  if (typed) scrollToId(typed);
                }
              }}
            />
          )}
          autoHighlight
          clearOnEscape
        />
      </FormControl>
    </>
  );
};

const outlineFade = keyframes`
  0% {
    outline-color: var(--search-highlight-color);
  }
  100% {
    outline-color: transparent;
  }
`;

const SCROLL_HIGHLIGHT_OFFSET = 50;
const SCROLL_HIGHLIGHT_DURATION = 4000;

export default SearchBar;
